import { emptyFilter, type DataSource, type ListQuery } from "@estelle/domain";

import { can } from "@/auth/permissions";
import { collectAll } from "@/components/table/export";
import { DIRECTORY_FIELDS } from "@/modules/contacts/directory/fields";
import { projectDirectoryEntry } from "@/modules/contacts/directory/projections";
import { DirectoryList } from "@/modules/contacts/directory/screens/DirectoryList";
import { CONTACT_FIELDS } from "@/modules/contacts/fields";
import {
  CUSTOMER_FIELD_PERMISSIONS,
  CUSTOMER_FIELDS,
} from "@/modules/contacts/customers/fields";
import { projectCustomer } from "@/modules/contacts/customers/projections";
import { CustomerList } from "@/modules/contacts/customers/screens/CustomerList";
import { projectContact } from "@/modules/contacts/projections";
import { contactsList } from "@/modules/contacts/screens/ContactList";
import { ContactTypes } from "@/modules/contacts/screens/ContactTypes";
import { withContactType, withVisibleKinds } from "@/modules/contacts/standing";
import {
  contactTypeFieldOptions,
  withTypeNames,
} from "@/modules/contacts/typeNames";

import type { ExportContext, ModuleUi } from "@/modules/types";

/**
 * A type section's export: the contacts its list shows.
 *
 * The type is looked up and applied the way the list does (`contactsList`,
 * `withContactType`). It was not applied at all, so Vendors, with 19 rows on
 * screen, downloaded all 40 contacts.
 */
function exportContactsOfType(typeName: string) {
  return async (data: DataSource, query: ListQuery) => {
    const type = (await data.contacts.listTypes()).find(
      (candidate) => candidate.name === typeName,
    );
    // The list fails loudly for the same reason: "a type that no longer exists"
    // is not the question "show me everyone".
    if (!type) throw new Error(`No contact type named "${typeName}" exists.`);

    return withTypeNames(
      data,
      await collectAll(
        (page) => data.contacts.list(page),
        {
          ...query,
          filter: withContactType(type.id, query.filter ?? emptyFilter()),
        },
        projectContact,
      ),
    );
  };
}

/**
 * The module's own sections. Vendors and Suppliers are the same list under a
 * standing type filter — the shape `modules/orders/index.ts` uses for
 * Unfulfilled. `types` is the odd one out: it manages the `ContactType` rows
 * the others filter by, so it is its own screen, and it declares no fields
 * because it is not a filterable list.
 *
 * `customers` is not a contact list at all (#447). It lists Shopify's
 * customers — mirrored, read-only, with the team's note and tags beside them —
 * so it has its own fields, projection and screens under `customers/`.
 *
 * `all` is the directory of both (#493): the contacts the team keeps by hand
 * and Shopify's customers in one list, with a Kind column and filter. Its rows
 * are neither a `Contact` nor a `CustomerSummary`, so it too has its own fields,
 * projection and screen, under `directory/`. A hand-kept contact of type
 * Customer (a B2B buyer) is still a contact there, reached by its type.
 *
 * Every export goes through the same narrowing its list does
 * (`contacts/standing.ts`).
 */
export const contactsModule: ModuleUi = {
  id: "contacts",
  sections: {
    all: {
      fields: DIRECTORY_FIELDS,
      exportRows: (
        data: DataSource,
        query: ListQuery,
        { permissions }: ExportContext,
      ) =>
        collectAll(
          (page) => data.contacts.directory(page),
          {
            ...query,
            filter: withVisibleKinds(
              can(permissions, "customers.view"),
              query.filter ?? emptyFilter(),
            ),
          },
          projectDirectoryEntry,
        ).then((rows) => withTypeNames(data, rows)),
      fieldOptions: contactTypeFieldOptions,
      screen: DirectoryList,
      commonFilters: ["kind", "city", "state", "tags"],
      // The server leaves out what the caller may not see, so the count is
      // theirs whatever is asked.
      count: async (data, query) =>
        (await data.contacts.directory({ ...query, limit: 1 })).total,
    },
    vendors: {
      fields: CONTACT_FIELDS,
      fieldOptions: contactTypeFieldOptions,
      exportRows: exportContactsOfType("Vendor"),
      screen: contactsList("Vendor"),
    },
    customers: {
      fields: CUSTOMER_FIELDS,
      fieldPermissions: CUSTOMER_FIELD_PERMISSIONS,
      exportRows: (data: DataSource, query: ListQuery) =>
        collectAll((page) => data.customers.list(page), query, projectCustomer),
      screen: CustomerList,
      count: async (data, query) =>
        (await data.customers.list({ ...query, limit: 1 })).total,
    },
    suppliers: {
      fields: CONTACT_FIELDS,
      fieldOptions: contactTypeFieldOptions,
      exportRows: exportContactsOfType("Supplier"),
      screen: contactsList("Supplier"),
    },
    types: { fields: [], screen: ContactTypes, padded: true },
  },
};
